import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { NewplaAiXhrOptions, newplaAiXhrPost } from './newpla-ai-xhr';

export type NewplaAiRole = 'USER' | 'ASSISTANT';

export interface NewplaAiChatRoomListDto {
  id: string;
  title: string;
  messageCount: number;
  createdAt: string;
  lastMessageAt: string;
}

export interface NewplaAiChatMessageDto {
  id: string;
  seq: number;
  role: NewplaAiRole;
  content: string;
  model?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  createdAt: string;
}

export interface NewplaAiChatRoomDetailDto {
  id: string;
  title: string;
  model?: string | null;
  messageCount: number;
  inputTokens: number;
  outputTokens: number;
  createdAt: string;
  lastMessageAt: string;
  messages: NewplaAiChatMessageDto[];
}

export interface NewplaAiChatSendResultDto {
  room: NewplaAiChatRoomListDto;
  question: NewplaAiChatMessageDto;
  answer: NewplaAiChatMessageDto;
}

export interface NewplaAiModel {
  /** Ollama 모델은 "ollama:" + 이름 */
  id: string;
  name: string;
  description?: string | null;
  /** gemini, ollama */
  provider: string;
}

/** 채팅 화면 설정 */
export interface NewplaAiChatOptionsDto {
  /** false면 기본 모델만 쓴다 */
  modelSelectable: boolean;
  defaultModel: string | null;
  /** 기본 모델이 맨 앞이다 */
  models: NewplaAiModel[];
  attachmentEnabled: boolean;
  attachmentMaxFileBytes: number;
  attachmentMaxFiles: number;
  /** 받는 종류 (mime). 서버가 내용으로 다시 확인한다 */
  attachmentTypes: string[];
}

/** AI 채팅방 API (/api/newpla-ai/chat-room) */
@Injectable({ providedIn: 'root' })
export class NewplaAiChatApi {

  private readonly baseUrl = '/api/newpla-ai/chat-room';
  private http = inject(HttpClient);

  findMyRooms(): Promise<NewplaAiChatRoomListDto[]> {
    return lastValueFrom(this.http.get<NewplaAiChatRoomListDto[]>(this.baseUrl));
  }

  options(): Promise<NewplaAiChatOptionsDto> {
    return lastValueFrom(this.http.get<NewplaAiChatOptionsDto>(`${this.baseUrl}/options`));
  }

  getMyRoom(roomId: string): Promise<NewplaAiChatRoomDetailDto> {
    return lastValueFrom(this.http.get<NewplaAiChatRoomDetailDto>(`${this.baseUrl}/${roomId}`));
  }

  /** 글만 보낸다. roomId가 없으면 새 방을 만든다. model이 없으면 기본 모델 */
  send(roomId: string | null, message: string, model: string | null): Promise<NewplaAiChatSendResultDto> {
    return lastValueFrom(this.http.post<NewplaAiChatSendResultDto>(this.messagesUrl(roomId), { message, model }));
  }

  /**
   * 글과 파일을 보낸다(multipart). 파일은 서버에 저장되지 않고 이 질문에만 AI로 간다.
   * 진행률·취소를 위해 HttpClient가 아니라 직접 만든 XHR(newpla-ai-xhr)로 보낸다
   */
  sendWithFiles(roomId: string | null, message: string, model: string | null, files: File[],
    options: NewplaAiXhrOptions): Promise<NewplaAiChatSendResultDto> {
    const form = new FormData();
    form.append('message', message);
    if (model) {
      form.append('model', model);
    }
    for (const file of files) {
      form.append('files', file, file.name);
    }
    return newplaAiXhrPost<NewplaAiChatSendResultDto>(this.messagesUrl(roomId), form, options);
  }

  rename(roomId: string, title: string): Promise<NewplaAiChatRoomListDto> {
    return lastValueFrom(this.http.put<NewplaAiChatRoomListDto>(`${this.baseUrl}/${roomId}/title`, { title }));
  }

  delete(roomId: string): Promise<void> {
    return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/${roomId}`));
  }

  private messagesUrl(roomId: string | null): string {
    return roomId ? `${this.baseUrl}/${roomId}/messages` : `${this.baseUrl}/messages`;
  }
}
